import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/pos-api";

const emptyDraft = () => ({ cart: [], discount: 0, customer: null, note: "", previousDebts: [], returnCredit: null });
const hasItems = (draft) => draft.cart?.length > 0 || draft.previousDebts?.length > 0;
const requestId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function useRefreshCart(tenantId, userId, enabled) {
  const storageKey = `pos_refresh_cart:${tenantId}:${userId}`;
  const [initial] = useState(() => {
    if (!enabled) return null;
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || "null");
      return saved?.version === 1 && saved.id && Array.isArray(saved.draft?.cart) && hasItems(saved.draft) ? saved : null;
    } catch { return null; }
  });
  const [posDraft, updateDraft] = useState(initial?.draft || emptyDraft);
  const current = useRef(initial);
  const recovery = useRef(initial);
  const inFlight = useRef(false);
  const [recovering, setRecovering] = useState(!!initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [storageError, setStorageError] = useState("");
  const draftRef = useRef(posDraft);

  const setPosDraft = useCallback((value) => {
    const next = typeof value === "function" ? value(draftRef.current) : value;
    draftRef.current = next;
    const saved = hasItems(next) ? { version: 1, id: requestId(), draft: next } : null;
    current.current = saved;
    if (enabled) {
      try {
        if (saved) sessionStorage.setItem(storageKey, JSON.stringify(saved));
        else sessionStorage.removeItem(storageKey);
        setStorageError("");
      } catch { setStorageError("Browser tidak dapat menyimpan keranjang. Masukkan ke Antrian sebelum refresh."); }
    }
    updateDraft(next);
  }, [enabled, storageKey]);

  const recover = useCallback(async () => {
    const saved = recovery.current;
    if (!saved || inFlight.current || !enabled) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    const draft = saved.draft;
    try {
      await api.post("/holds", {
        label: `Refresh · ${draft.customer?.name || "Keranjang"}`,
        client_request_id: saved.id,
        payload: { items: draft.cart, discount: draft.discount || 0, customer: draft.customer || null, note: draft.note || "", previousDebts: draft.previousDebts || [], returnCredit: draft.returnCredit || null },
      }, { timeout: 15000 });
      // Clear the saved snapshot only after the server acknowledges the queue entry.
      setPosDraft(emptyDraft());
      recovery.current = null;
      setRecovering(false);
      toast.success("Keranjang sebelum refresh sudah masuk ke Antrian");
    } catch (cause) {
      setError(apiError(cause));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [enabled, setPosDraft]);

  useEffect(() => {
    recover();
    window.addEventListener("online", recover);
    return () => window.removeEventListener("online", recover);
  }, [recover]);

  return { posDraft, setPosDraft, draftRequestId: current.current?.id, recovering, busy, error, storageError, recover };
}
